import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ExportButton } from "@/components/ExportButton";
import { ArrowDownToLine, ArrowUpFromLine, DollarSign, Receipt, ChevronLeft, ChevronRight } from "lucide-react";

type GroupRow = { id: string; name: string | null };

type DecisionRow = {
  group_id: string | null;
  input_tokens: number | null;
  output_tokens: number | null;
  estimated_cost_usd: number | null;
  model_used: string | null;
};

type ReportRow = {
  group_id: string;
  group_name: string;
  calls: number;
  input_tokens: number;
  output_tokens: number;
  total_tokens: number;
  cost_usd: number;
};

function monthKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function monthLabel(key: string): string {
  const [y, m] = key.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString("pt-BR", {
    month: "long",
    year: "numeric",
  });
}

function monthRange(key: string): { start: string; end: string } {
  const [y, m] = key.split("-").map(Number);
  const start = new Date(y, m - 1, 1);
  const end = new Date(y, m, 1);
  return { start: start.toISOString(), end: end.toISOString() };
}

function shiftMonth(key: string, delta: number): string {
  const [y, m] = key.split("-").map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return monthKey(d);
}

function fmtNumber(n: number): string {
  return n.toLocaleString("pt-BR");
}

function fmtCost(n: number): string {
  return n.toLocaleString("pt-BR", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  });
}

export default function GroupsBilling() {
  const [month, setMonth] = useState<string>(() => monthKey(new Date()));
  const currentMonth = monthKey(new Date());

  const { data: groups = [] } = useQuery({
    queryKey: ["billing-groups"],
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("syra_groups")
        .select("id, name")
        .order("name");
      if (error) throw error;
      return (data ?? []) as GroupRow[];
    },
  });

  const { data: decisions = [], isLoading } = useQuery({
    queryKey: ["billing-decisions", month],
    staleTime: 60_000,
    queryFn: async () => {
      const { start, end } = monthRange(month);
      const { data, error } = await supabase
        .from("model_decisions")
        .select("group_id, input_tokens, output_tokens, estimated_cost_usd, model_used")
        .gte("created_at", start)
        .lt("created_at", end)
        .not("group_id", "is", null)
        .limit(50000);
      if (error) throw error;
      return (data ?? []) as DecisionRow[];
    },
  });

  const report = useMemo<ReportRow[]>(() => {
    const groupMap = new Map(groups.map((g) => [g.id, g.name ?? "(sem nome)"]));
    const agg = new Map<string, ReportRow>();
    for (const r of decisions) {
      if (!r.group_id) continue;
      const cur =
        agg.get(r.group_id) ??
        ({
          group_id: r.group_id,
          group_name: groupMap.get(r.group_id) ?? "(grupo removido)",
          calls: 0,
          input_tokens: 0,
          output_tokens: 0,
          total_tokens: 0,
          cost_usd: 0,
        } as ReportRow);
      cur.calls += 1;
      cur.input_tokens += r.input_tokens ?? 0;
      cur.output_tokens += r.output_tokens ?? 0;
      cur.total_tokens += (r.input_tokens ?? 0) + (r.output_tokens ?? 0);
      cur.cost_usd += Number(r.estimated_cost_usd ?? 0);
      agg.set(r.group_id, cur);
    }
    return Array.from(agg.values()).sort((a, b) => b.cost_usd - a.cost_usd);
  }, [decisions, groups]);

  const totals = useMemo(() => {
    return report.reduce(
      (acc, r) => {
        acc.calls += r.calls;
        acc.input += r.input_tokens;
        acc.output += r.output_tokens;
        acc.cost += r.cost_usd;
        return acc;
      },
      { calls: 0, input: 0, output: 0, cost: 0 },
    );
  }, [report]);

  const exportRows = useMemo(
    () =>
      report.map((r) => ({
        ...r,
        period: month,
        cost_usd: r.cost_usd.toFixed(6),
      })),
    [report, month],
  );

  return (
    <div className="container mx-auto p-6 space-y-6 max-w-7xl">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Receipt className="w-7 h-7 text-primary" />
            Relatório mensal por grupo
          </h1>
          <p className="text-muted-foreground mt-1">
            Tokens consumidos e custo estimado por grupo no mês — pronto para
            cobrança de mensalidade.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={() => setMonth((m) => shiftMonth(m, -1))}
            aria-label="Mês anterior"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Input
            type="month"
            value={month}
            max={currentMonth}
            onChange={(e) => setMonth(e.target.value || currentMonth)}
            className="w-[180px]"
          />
          <Button
            variant="outline"
            size="icon"
            onClick={() => setMonth((m) => shiftMonth(m, 1))}
            disabled={month >= currentMonth}
            aria-label="Próximo mês"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
          <ExportButton
            data={exportRows}
            filename={`relatorio-grupos-${month}`}
            columns={[
              { key: "period", label: "Período" },
              { key: "group_id", label: "Group ID" },
              { key: "group_name", label: "Grupo" },
              { key: "calls", label: "Chamadas" },
              { key: "input_tokens", label: "Tokens Input" },
              { key: "output_tokens", label: "Tokens Output" },
              { key: "total_tokens", label: "Tokens Total" },
              { key: "cost_usd", label: "Custo (USD)" },
            ]}
          />
        </div>
      </div>

      <p className="text-sm text-muted-foreground capitalize">
        Período: {monthLabel(month)}
      </p>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <SummaryCard
          icon={<Receipt className="w-4 h-4" />}
          label="Chamadas IA"
          value={fmtNumber(totals.calls)}
          loading={isLoading}
        />
        <SummaryCard
          icon={<ArrowDownToLine className="w-4 h-4" />}
          label="Tokens Input"
          value={fmtNumber(totals.input)}
          loading={isLoading}
        />
        <SummaryCard
          icon={<ArrowUpFromLine className="w-4 h-4" />}
          label="Tokens Output"
          value={fmtNumber(totals.output)}
          loading={isLoading}
        />
        <SummaryCard
          icon={<DollarSign className="w-4 h-4" />}
          label="Custo total"
          value={fmtCost(totals.cost)}
          highlight
          loading={isLoading}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            Detalhamento por grupo ({report.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : report.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">
              Nenhuma chamada de IA registrada com group_id neste período.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Grupo</TableHead>
                  <TableHead className="text-right">Chamadas</TableHead>
                  <TableHead className="text-right">Input</TableHead>
                  <TableHead className="text-right">Output</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="text-right">Custo (USD)</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {report.map((r) => (
                  <TableRow key={r.group_id}>
                    <TableCell className="font-medium">
                      <div>{r.group_name}</div>
                      <div className="text-[10px] text-muted-foreground font-mono">
                        {r.group_id.slice(0, 8)}…
                      </div>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {fmtNumber(r.calls)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {fmtNumber(r.input_tokens)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {fmtNumber(r.output_tokens)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {fmtNumber(r.total_tokens)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums font-semibold text-primary">
                      {fmtCost(r.cost_usd)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function SummaryCard({
  icon,
  label,
  value,
  highlight,
  loading,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  highlight?: boolean;
  loading?: boolean;
}) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
          {icon} {label}
        </div>
        {loading ? (
          <Skeleton className="h-8 w-24 mt-2" />
        ) : (
          <div
            className={`text-2xl font-bold tabular-nums mt-1 ${highlight ? "text-primary" : ""}`}
          >
            {value}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
